import clsx from 'clsx';
import React, { useState } from 'react';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import AddIcon from '../../assets/icons/add-icon.svg?react';
import { toggleSwitch } from '../../reducers/switch';
import { useDispatch, useSelector } from 'react-redux';
import SearchInput from '../../components/SearchInput';
import { toggleModal } from '../../reducers/modalToggle';
import { useFormik } from 'formik';
import {
  getDeleteDepartmentSchema,
  getDepartmentSchema,
  getEditDepartmentDetailsSchema,
} from './schema';
import ModalComponent from '../../components/ModalComponent';
import EditDepartmentDetailsModal from '../../components/EditDepartmentDetailsModal';
import AddDepartmentModal from '../../components/AddDepartmentModal/Index';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClients } from '../../client/api/useApiClient';
import { toast } from 'react-toastify';
import Button from '../../components/Button';
import {
  DepartmentWithCount,
  GetAllDepartmentResponse,
} from '@EHSNavigator/backend';
import { queryClient } from '../../main';
import { useNavigate, useSearchParams } from 'react-router';
import { useDebounce, useLocalStorage } from '@uidotdev/usehooks';
import Loader from '../../components/Loader';
import Pagination from '../../components/CustomPagination';
import DeleteDepartmentModal from '../../components/DeleteDepartmentModal';
import NoItemsFound from '../../components/NoItemsFound';
import DropDown from '../../components/DropDown';
import { UserWithOrgContext } from '../../types/user';

interface DepartmentsProps {
  otherClasses?: string;
}

const INITIAL_ADD_DEPARTMENT_STATE = {
  name: '',
  prefix: '',
};

const INITIAL_DELETE_DEPARTMENT_STATE = {
  name: '',
};

const Departments: React.FC<DepartmentsProps> = ({ otherClasses }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') ?? '';
  const rawPage = searchParams.get('page');
  const currentPage = rawPage && !isNaN(Number(rawPage)) ? Number(rawPage) : 1;
  const limit = Number(searchParams.get('limit')) || 10;
  const hasFilters = !!searchQuery || currentPage != 1;

  const noDataMessage = hasFilters
    ? 'No items match your filters or search. Try using different keywords maybe.'
    : 'Departments does not exist';

  const updateQuery = (queryName: string, queryValue: string) => {
    const params = new URLSearchParams(window.location.search);

    if (queryName === 'search') {
      params.set('page', '1');
    }

    if (queryName === 'page' && queryValue === '1') {
      params.delete('page');
    } else {
      params.set(queryName, queryValue);
    }

    navigate(`/departments?${params.toString()}`);
  };

  const departmentsClasses = clsx('flex flex-1 flex-col w-full' + otherClasses);
  const [currentDepartment, setIsCurrentDepartment] =
    useState<DepartmentWithCount | null>(null);
  const [state, setState] = useState({
    search: searchQuery,
    limit: Number(limit),
    page: Number(currentPage),
  });

  const debouncedSearch = useDebounce(state.search, 600);
  const { DepartmentApi } = useApiClients();
  const [userInfo] = useLocalStorage<UserWithOrgContext | null>('userInfo', null);
  const organizationId = userInfo?.orgContext?.org.id || undefined;

  const dispatch = useDispatch();
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );

  const { data, isLoading, refetch } = useQuery<
    GetAllDepartmentResponse,
    Error,
    any
  >({
    queryKey: ['departments', debouncedSearch, state.page, state.limit, organizationId],
    queryFn: async () => {
      const params: any = {
        search: debouncedSearch || undefined,
        limit: state.limit,
        page: state.page,
        ...(organizationId ? { organizationId } : {}),
      };
      const response = await DepartmentApi.departmentControllerFindAllDepartment(params);
      return response?.data || [];
    },
    enabled: debouncedSearch.length > 0 || state.search.length === 0,
  });

  const addDepartmentMutation = useMutation({
    mutationFn: async ({ name, prefix }: { name: string; prefix: string }) => {
      const createParams: any = {
        createDepartmentDTO: {
          name,
          prefix,
          ...(organizationId ? { organizationId } : {}),
        },
      };
      return await DepartmentApi.departmentControllerCreate(createParams);
    },
    onSuccess: (data: any) => {
      const newDepartment = data?.data?.department;
      toast.success(data?.data?.message);

      queryClient.setQueryData(
        ['departments', debouncedSearch, state.page, state.limit, organizationId],
        (oldData: any) => {
          return {
            ...oldData,
            departments: [
              { ...newDepartment, employeeCount: 0 },
              ...oldData.departments,
            ],
          };
        },
      );

      dispatch(toggleModal(''));
      addDepartmentHandler?.resetForm();
      setIsCurrentDepartment(null);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });
  const addDepartmentHandler = useFormik({
    initialValues: INITIAL_ADD_DEPARTMENT_STATE,
    validationSchema: getDepartmentSchema(),
    onSubmit: async (values) => {
      try {
        await addDepartmentMutation.mutateAsync({
          name: values.name,
          prefix: values.prefix.toUpperCase(),
        });
        addDepartmentHandler.resetForm();
      } catch (error) {
        console.error('Failed to add department:', error);
      }
    },
  });

  const formHandler = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: String(currentDepartment?.name),
      prefix: String(currentDepartment?.prefix),
    },
    validationSchema: getEditDepartmentDetailsSchema(
      currentDepartment as DepartmentWithCount,
    ),
    onSubmit: async (values) => {
      try {
        const updateParams: any = {
          id: String(currentDepartment?.id),
          updateDepartmentDTO: {
            name: values?.name,
            prefix: values?.prefix,
            ...(organizationId ? { organizationId } : {}),
          },
        };
        const res = await DepartmentApi.departmentControllerUpdate(updateParams);
        refetch();
        toast.success(res?.data?.message);
        setIsCurrentDepartment(null);
        dispatch(toggleModal(''));
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const deleteDepartmentHandler = useFormik({
    initialValues: INITIAL_DELETE_DEPARTMENT_STATE,
    validationSchema: getDeleteDepartmentSchema(currentDepartment?.name || ''),
    onSubmit: async () => {
      try {
        const deleteParams: any = {
          id: String(currentDepartment?.id),
          ...(organizationId ? { organizationId } : {}),
        };
        const res = await DepartmentApi.departmentControllerRemove(deleteParams);
        queryClient.setQueryData(
          ['departments', debouncedSearch, state.page, state.limit, organizationId],
          (oldData: any) => {
            if (!oldData) return [];
            const data = oldData?.departments.filter(
              (dept: DepartmentWithCount) => currentDepartment?.id !== dept.id,
            );
            return { ...oldData, departments: data };
          },
        );
        toast.success(res?.data?.message);
        dispatch(toggleModal(''));
        setIsCurrentDepartment(null);
        deleteDepartmentHandler?.resetForm();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateQuery('search', e?.target?.value);
    setState((prev) => ({
      ...prev,
      page: 1,
      search: e?.target?.value,
    }));
  };

  const handlePageChange = (newPage: number) => {
    setState((prev) => ({ ...prev, page: newPage }));
    updateQuery('page', String(newPage));
  };

  const lastTwoIndex = data?.departments?.slice(-2);

  return (
    <div className={departmentsClasses} data-testid="departments">
      <div className="flex items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div className="flex w-full flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
          <div>
            <h1 className="h3">Departments</h1>
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
              Manage all departments in your company.
            </p>
          </div>
          <Button
            size="small"
            otherClasses="hidden lg:flex"
            variant="primary"
            onClick={() => dispatch(toggleModal('add-department'))}
          >
            <AddIcon className="h-4 w-4 md:h-8 md:w-8 [&>path]:fill-current" />
            Add New Department
          </Button>
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 mb-15 text-lg leading-5 font-medium lg:hidden">
        Manage all departments in your company.
      </p>
      <Button
        size="smaller"
        otherClasses="lg:hidden"
        variant="primary"
        onClick={() => dispatch(toggleModal('add-department'))}
      >
        <AddIcon className="h-4 w-4 md:h-8 md:w-8 [&>path]:fill-current" />
        Add New Department
      </Button>
      <hr className="border-neutral-tones-text/50 mt-9 mb-6 lg:hidden" />
      <SearchInput
        name="search"
        onChange={handleSearchChange}
        placeholder="Enter department name or prefix…"
        value={state?.search}
        disabled={false}
        errorMessage=""
        otherClasses={{ container: 'w-full lg:mt-12 ' }}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {data?.departments?.length > 0 ? (
            <div className="global-scrollbar-style mt-12 min-h-52 flex-1 overflow-auto px-0.5">
              <div className="w-full min-w-3xl lg:min-w-4xl">
                <div className="border-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b pb-6">
                  <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                    Sr.
                  </h6>
                  <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:col-span-1 lg:text-lg lg:leading-5">
                    Prefix
                  </h6>
                  <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-4 font-bold lg:col-span-6 lg:text-lg lg:leading-5">
                    Department Name
                  </h6>
                  <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                    Employees
                  </h6>
                  <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-3 font-bold lg:col-span-2 lg:text-lg lg:leading-5">
                    Date Created
                  </h6>
                </div>
                <div>
                  {data?.departments.map(
                    (dept: DepartmentWithCount, index: any) => (
                      <div
                        key={dept.id}
                        className="border-b-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b py-5"
                      >
                        <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-1 flex items-center text-sm leading-4 font-bold lg:text-base lg:leading-5 lg:font-normal">
                          {data?.meta?.page > 1 &&
                            `${index < 9 ? data?.meta?.page - 1 : data?.meta?.page}`}
                          {index < 9
                            ? index + 1
                            : data?.meta?.page > 1
                              ? 0
                              : index + 1}
                        </p>
                        <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal lg:col-span-1 lg:text-base lg:leading-5">
                          {dept.prefix}
                        </p>
                        <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-4 flex items-center text-sm leading-4 font-normal capitalize lg:col-span-6 lg:text-base lg:leading-5">
                          {dept.name}
                        </p>
                        <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                          {dept.employeeCount}
                        </p>
                        <div className="col-span-3 flex items-center justify-between lg:col-span-2">
                          <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input text-sm leading-4 font-normal lg:text-base lg:leading-5">
                            {new Date(dept.createdAt).toLocaleDateString(
                              'en-US',
                              {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              },
                            )}
                          </p>
                          <DropDown
                            options={[
                              {
                                label: 'Edit',
                                onClick: (args: any) => {
                                  setIsCurrentDepartment(args);
                                  dispatch(
                                    toggleModal('edit-department-details'),
                                  );
                                },
                                onOptionClickArgs: dept,
                                hidden: false,
                              },
                              {
                                label: 'Delete',
                                onClick: (args: any) => {
                                  setIsCurrentDepartment(args);
                                  dispatch(toggleModal('delete-department'));
                                },
                                onOptionClickArgs: dept,
                                hidden: false,
                              },
                            ]}
                            additionalClasses={clsx(
                              data.departments.length < 4
                                ? ''
                                : lastTwoIndex.some(
                                  (node: DepartmentWithCount) =>
                                    node?.id === dept?.id,
                                )
                                  ? 'bottom-10 top-auto max-w-24'
                                  : 'max-w-24',
                            )}
                          />
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          ) : (
            <NoItemsFound message={noDataMessage} />
          )}
        </>
      )}
      {data?.departments?.length > 0 && (
        <Pagination
          currentPage={state.page}
          totalPages={data?.meta?.totalPages}
          totalItems={data?.meta?.total}
          limit={data?.meta?.limit}
          showingItems={data?.departments?.length}
          onPageChange={handlePageChange}
          onLimitChange={(e) =>
            setState((prev) => ({ ...prev, limit: e, page: 1 }))
          }
        />
      )}
      {[
        'edit-department-details',
        'delete-department',
        'add-department',
      ].includes(modalName) && (
          <ModalComponent
            otherClasses="px-4 sm:px-0 "
            additionalClass={clsx(
              'w-full max-w-573 rounded-2xl p-5 lg:px-9 lg:py-6 ',
            )}
            customClose={() => {
              setIsCurrentDepartment(null);
              dispatch(toggleModal(''));
            }}
          >
            {modalName === 'add-department' && (
              <AddDepartmentModal
                formHandler={addDepartmentHandler}
                handleClose={() => {
                  addDepartmentHandler.resetForm();
                  dispatch(toggleModal(''));
                }}
              />
            )}
            {modalName === 'edit-department-details' && currentDepartment && (
              <EditDepartmentDetailsModal
                currentDepartment={currentDepartment}
                formHandler={formHandler}
                handleClose={() => {
                  dispatch(toggleModal(''));
                  setIsCurrentDepartment(null);
                  formHandler.resetForm();
                }}
              />
            )}
            {modalName === 'delete-department' && (
              <DeleteDepartmentModal
                currentDepartment={currentDepartment}
                formHandler={deleteDepartmentHandler}
                handleClose={() => {
                  setIsCurrentDepartment(null);
                  dispatch(toggleModal(''));
                  deleteDepartmentHandler?.resetForm();
                }}
              />
            )}
          </ModalComponent>
        )}
    </div>
  );
};

export default Departments;
